/*
  Sunset Editorial — poster-frame.js
  « Mise sous cadre » au survol des affiches (cartes produit, liste de collections).

  Une seule progression q (0 = repos, 1 = affiche encadrée au mur), qui avance au survol et recule
  quand le pointeur s'en va : un aller-retour interrompu repart toujours de l'état affiché.
  - Phase A (q de 0 à 42 %) : l'affiche recule, toujours de face, de 100 % à 85 % de son espace ;
    un mur blanc apparaît derrière, avec l'ombre d'une affiche encore loin du mur.
  - Phase B (42 % à 100 %) : l'affiche garde ses 85 % et se glisse dans un cadre noir ; l'ombre se
    resserre jusqu'au contact : elle touche le mur.
  Le rendu est en CSS (variables --pf-a et --pf-b, de 0 à 1) ; ce script ne fait qu'avancer q.
  Tactile : appui long (comme une Live Photo), un tap ouvre toujours la fiche. Clavier : au focus.
  Écran tactile, mode « au défilement » (par défaut) : sans survol possible, l'affiche se met sous
  cadre d'elle-même quand elle passe au milieu de l'écran, et en ressort quand elle s'en éloigne.
*/
(() => {
  'use strict';

  if (customElements.get('poster-frame')) return;

  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
  const inOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2);
  const SPLIT = 0.42; // part du recul dans l'animation
  const RETURN_SPEED = 1.6; // le retour au repos est plus rapide que l'aller
  const HOVER_INTENT = 60; // ms : balayer la grille ne déclenche rien
  const PRESS_DELAY = 160; // ms d'appui immobile sur écran tactile
  const PRESS_SLOP = 10; // px tolérés pendant l'appui
  const TOUCH_SCREEN = '(hover: none) and (pointer: coarse)';
  const VIEW_DELAY = 90; // ms au milieu de l'écran avant la mise sous cadre (évite les à-coups d'un défilement rapide)

  // Un seul observateur pour toutes les affiches : une bande fine au milieu de l'écran (16 % de sa
  // hauteur). Une affiche qui la traverse se met sous cadre ; en pratique, une rangée à la fois.
  const viewers = new Map();
  let viewObserver = null;
  const observeView = (element, instance) => {
    viewObserver =
      viewObserver ||
      new IntersectionObserver(
        (entries) => entries.forEach((entry) => viewers.get(entry.target)?.inView(entry.isIntersecting)),
        { rootMargin: '-42% 0px -42% 0px' }
      );
    viewers.set(element, instance);
    viewObserver.observe(element);
  };
  const unobserveView = (element) => {
    if (!viewers.delete(element)) return;
    viewObserver?.unobserve(element);
  };

  class PosterFrame extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.host = this.parentElement;
      this.root = this.closest('a') || this.host;
      if (!this.host) return;
      this.ready = true;
      this.duration = clamp(parseFloat(this.dataset.duration) || 1.2, 0.4, 4) * 1000;
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.q = 0;
      this.target = 0;
      this.cleanups = [];

      const on = (type, handler, options) => {
        this.root.addEventListener(type, handler, options);
        this.cleanups.push(() => this.root.removeEventListener(type, handler, options));
      };
      on('pointerenter', (event) => {
        if (event.pointerType === 'touch') return;
        clearTimeout(this.intent);
        this.intent = setTimeout(() => this.setTarget(1), HOVER_INTENT);
      });
      on('pointerleave', (event) => {
        if (event.pointerType === 'touch') return;
        clearTimeout(this.intent);
        this.setTarget(0);
      });
      on('pointerdown', (event) => {
        if (event.pointerType !== 'touch' || !event.isPrimary) return;
        this.pressOrigin = { x: event.clientX, y: event.clientY };
        clearTimeout(this.pressTimer);
        this.pressTimer = setTimeout(() => {
          this.pressTimer = null;
          this.pressed = true;
          this.setTarget(1);
        }, PRESS_DELAY);
      });
      on('pointermove', (event) => {
        if (event.pointerType !== 'touch' || !this.pressTimer || !this.pressOrigin) return;
        if (Math.hypot(event.clientX - this.pressOrigin.x, event.clientY - this.pressOrigin.y) > PRESS_SLOP) this.cancelPress();
      });
      on('pointerup', (event) => {
        if (event.pointerType !== 'touch') return;
        if (this.pressed) {
          // L'aperçu a été vu : le relâchement n'ouvre pas la fiche
          this.suppressClick = true;
          clearTimeout(this.clickTimer);
          this.clickTimer = setTimeout(() => (this.suppressClick = false), 450);
        }
        this.cancelPress();
      });
      on('pointercancel', () => this.cancelPress());
      on('contextmenu', (event) => {
        if (this.pressed || this.pressTimer) event.preventDefault();
      });
      on(
        'click',
        (event) => {
          if (!this.suppressClick) return;
          this.suppressClick = false;
          event.preventDefault();
          event.stopPropagation();
        },
        true
      );
      on('focusin', () => {
        if (this.root.matches(':focus-visible')) this.setTarget(1);
      });
      on('focusout', () => this.setTarget(0));
      this.root.classList.add('poster-frame-root');

      // Écran tactile : mise sous cadre au passage dans le milieu de l'écran
      if (this.dataset.mobile !== 'press' && window.matchMedia(TOUCH_SCREEN).matches) observeView(this.host, this);
    }

    inView(visible) {
      this.inBand = visible;
      clearTimeout(this.viewTimer);
      if (visible) this.viewTimer = setTimeout(() => this.setTarget(1), VIEW_DELAY);
      else if (!this.pressed) this.setTarget(0);
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.raf);
      this.raf = 0;
      [this.intent, this.pressTimer, this.clickTimer, this.viewTimer].forEach(clearTimeout);
      if (this.host) unobserveView(this.host);
      (this.cleanups || []).forEach((cleanup) => cleanup());
      this.cleanups = [];
      this.root?.classList.remove('poster-frame-root');
      this.ready = false;
    }

    cancelPress() {
      clearTimeout(this.pressTimer);
      this.pressTimer = null;
      this.pressOrigin = null;
      if (this.pressed) {
        this.pressed = false;
        // Au milieu de l'écran (mode au défilement), l'affiche reste sous cadre après l'appui
        this.setTarget(this.inBand ? 1 : 0);
      }
    }

    setTarget(value) {
      if (!this.ready || (value > 0 && this.reduced.matches)) return;
      this.target = value;
      if (this.raf || this.q === this.target) return;
      this.host.classList.add('is-framing');
      this.last = performance.now();
      this.raf = requestAnimationFrame((now) => this.frame(now));
    }

    frame(now) {
      const dt = Math.min(now - this.last, 64);
      this.last = now;
      const forward = this.target > this.q;
      const step = (dt / this.duration) * (forward ? 1 : RETURN_SPEED);
      this.q = forward ? Math.min(this.target, this.q + step) : Math.max(this.target, this.q - step);
      this.render();
      if (this.q !== this.target) {
        this.raf = requestAnimationFrame((time) => this.frame(time));
        return;
      }
      this.raf = 0;
      if (this.q === 0) this.host.classList.remove('is-framing');
    }

    render() {
      // Chaque phase part et s'arrête en douceur : petite pause naturelle à 85 %, avant le cadre
      const a = inOut(clamp(this.q / SPLIT, 0, 1));
      const b = inOut(clamp((this.q - SPLIT) / (1 - SPLIT), 0, 1));
      this.host.style.setProperty('--pf-a', a.toFixed(4));
      this.host.style.setProperty('--pf-b', b.toFixed(4));
    }
  }

  customElements.define('poster-frame', PosterFrame);
})();
